import IconAdd from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import ConfirmDialog from 'components/ConfirmDialog'
import EditableLabel from 'components/EditableLabel'
import { PPDrawer } from 'components/PPDrawer'
import ProfileAvatarGridList from 'components/ProfileAvatarGridList'
import Loader from 'components/SimpleLoader'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ALL_LIST_KEY } from 'services/lists/selectors'
import { List, PostDestination, sortByKeyAscending } from 'shared'
import styles from './ListEditorDialog.pcss'
const LOADER_SIZE_SM = 16

export interface ListEditorDialogProps {
  open: boolean
  lists: { [id: string]: List }
  profiles: { [id: string]: PostDestination }
  className?: string
  loading?: boolean
  creatingList?: boolean
  onClose: () => void
  onCreateList: (name: string, profiles: string[]) => void
  onUpdateList: (id: string, name: string, profiles: string[]) => void
  onDeleteList: (id: string) => void
}

export function ListEditorDialog(props: ListEditorDialogProps) {
  const intl = useIntl()
  const [activeEditListId, setActiveEditListId] = React.useState<string | undefined>(undefined)
  const [isCreatingList, setIsCreatingList] = React.useState(false)
  const [newListName, setNewListName] = React.useState('')
  const [newListError, setNewListError] = React.useState('')
  const [newListProfiles, setNewListProfiles] = React.useState<string[]>([])
  const activeEditList = activeEditListId ? props.lists[activeEditListId] : undefined

  const handleEditListClick = (id: string) => () => {
    setActiveEditListId(id)
    setNewListName('')
    setIsCreatingList(false)
    setNewListError('')
    setNewListProfiles([])
  }

  const handleNewListNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNewListName(value)
  }

  const handleNewListInputFocus = () => {
    setActiveEditListId(undefined)
    setNewListError('')
  }

  const handleNewListSave = () => {
    if (props.creatingList) {
      return
    }
    const name = newListName.trim()
    if (!name) {
      setNewListError(intl.formatMessage({ id: 'list-manager.labels.error-empty-name' }))
      return
    }
    if (newListProfiles.length === 0) {
      setNewListError(intl.formatMessage({ id: 'list-manager.labels.error-empty-profiles' }))
      return
    }

    props.onCreateList(name, newListProfiles)
    setNewListError('')
    setNewListName('')
    setNewListProfiles([])
    setIsCreatingList(false)
  }

  const onEditListProfileClick = (id: string) => {
    if (activeEditList?.id === ALL_LIST_KEY) {
      return
    }
    if (activeEditList) {
      const i = activeEditList.pages.indexOf(id)
      const pages = activeEditList.pages.slice()
      if (i !== -1) {
        pages.splice(i, 1)
      } else {
        pages.push(id)
      }
      props.onUpdateList(activeEditList.id, activeEditList.name, pages)
      return
    }

    const idIndex = newListProfiles.indexOf(id)
    const nextIds = newListProfiles.slice()
    if (idIndex !== -1) {
      nextIds.splice(idIndex, 1)
    } else {
      nextIds.push(id)
    }
    setNewListProfiles(nextIds)
  }

  const onListNameChange = (name?: string) => {
    if (name && activeEditList) {
      props.onUpdateList(activeEditList.id, name, activeEditList.pages)
    }
  }

  const onDeleteListClick = () => {
    if (activeEditList) {
      props.onDeleteList(activeEditList.id)
      setActiveEditListId(undefined)
    }
  }

  const toggleCreateListMode = () => {
    setIsCreatingList(current => !current)
    setActiveEditListId(undefined)
  }

  const onDoneEditing = () => {
    setActiveEditListId(undefined)
  }

  return (
    <PPDrawer
      open={props.open}
      onClose={props.onClose}
      size="md"
      data-testid="list-editor-dialog"
      title={<FormattedMessage id="list-manager.labels.create-edit-lists" />}
    >
      {props.loading && <Loader />}
      {!props.loading && (
        <section
          data-test="list-manager"
          className={`${styles.container} ${props.className || ''}`}
        >
          <div className={styles.content}>
            <div className={styles['lists-box']}>
              <div className={styles.section}>
                <h3 className={styles['section-title']}>
                  <span>
                    <FormattedMessage id="list-manager.labels.edit" />
                  </span>
                  {Boolean(activeEditListId) && (
                    <Button variant="contained" color="primary" size="small" onClick={onDoneEditing}>
                      <FormattedMessage id="actions.done" />
                    </Button>
                  )}
                </h3>
                <ul className={styles['ul-lists']}>
                  {
                    Object.values(props.lists).sort(sortByKeyAscending('order')).map(list => {
                      const active = activeEditList && activeEditList.id === list.id
                      return (
                        <li
                          key={list.id}
                          className={`text-ellipsis ${styles['li-list-name']} ${active ? styles.active : ''}`}
                          onClick={handleEditListClick(list.id)}
                        >
                          <EditableLabel
                            value={list.name}
                            editMode={list.id === ALL_LIST_KEY ? false : Boolean(active)}
                            inputClassName={styles['input-edit-list']}
                            className={styles['input-label']}
                            onChange={onListNameChange}
                          />
                          {(!active || list.id === ALL_LIST_KEY) && (
                            <span className={styles.count}>{`(${list.connectedPages.length})`}</span>
                          )}
                          {
                            list.id !== ALL_LIST_KEY && (
                              <ConfirmDialog
                                message={intl.formatMessage({ id: 'list-manager.labels.confirm-delete' })}
                                variant="danger"
                                confirmLabel={intl.formatMessage({ id: 'actions.delete' })}
                              >
                                {(confirm) => (
                                  <DeleteIcon role="button" className={styles['btn-delete']} onClick={confirm(onDeleteListClick)} />
                                )}
                              </ConfirmDialog>
                            )
                          }
                        </li>
                      )
                    })
                  }
                </ul>
              </div>
              <div className={`${styles.section} ${styles['section-create']} ${isCreatingList ? styles.active : ''}`}>
                <h3 className={styles['section-title']} onClick={toggleCreateListMode}>
                  <span>
                    <FormattedMessage id="list-manager.labels.create-new" />
                  </span>
                  {!isCreatingList && <IconAdd className={styles['icon-add']} />}
                </h3>
                {isCreatingList && (
                  <div className={styles['input-box']}>
                    <TextField
                      error={Boolean(newListError)}
                      helperText={newListError}
                      value={newListName}
                      InputProps={{
                        classes: {
                          input: styles.input,
                          root: styles['input-root']
                        }
                      }}
                      placeholder={intl.formatMessage({ id: 'list-manager.labels.new-list-hint' })}
                      onChange={handleNewListNameChange}
                      onFocus={handleNewListInputFocus}
                    />
                    <Button
                      disabled={props.creatingList}
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleNewListSave}
                    >
                      {props.creatingList ? <CircularProgress size={LOADER_SIZE_SM} /> : <FormattedMessage id="actions.save" />}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className={styles['profiles-box']}>
              <div className={styles.section}>
                <h3 className={styles['section-title']}>
                  <FormattedMessage id="list-manager.labels.profiles-in-list" />
                  {isCreatingList && ` (${newListProfiles.length})`}
                  {activeEditList && ` (${activeEditList.connectedPages.length})`}
                </h3>
                {(isCreatingList || activeEditList) ? (
                  <ProfileAvatarGridList
                    profiles={props.profiles}
                    selectedIds={activeEditList ? activeEditList.pages : newListProfiles}
                    className={`${styles['profiles-grid']} ${activeEditList?.id === ALL_LIST_KEY ? styles.readonly : ''}`}
                    itemClassName={styles['grid-item']}
                    onProfileClick={onEditListProfileClick}
                  />
                ) : (
                  <p className={styles.message}>
                    <FormattedMessage id="list-manager.labels.list-not-selected" />
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </PPDrawer>
  )
}

export default ListEditorDialog
